import { forwardRef, useEffect, useLayoutEffect, useRef } from "react";
import type { MouseEvent, ReactElement, ReactNode, Ref, RefObject } from "react";
import { Eingabe, Knopf, Register, Seitenleiste } from "@kundeninfo/ui/me-design";
import type { KnopfProps, RegisterProps } from "@kundeninfo/ui/me-design";
import "./meHuellen.css";

/*
 * Kundeninfo-Zutaten zu den ME-Elementen (ME-Etappe 4, P4) — an EINER Stelle, damit die
 * Kundenakte, die Listen und der Belegdesigner dieselben benutzen.
 *
 * Seit den Tarbällen a59b8aa/8cc13b0 (P4b) bringt me-design selbst mit, was hier bis dahin
 * als Übergang stand: `platzhalter` an me-eingabe (P3-1), `aria-label` am me-knopf, das das
 * Element auf seinen inneren Knopf spiegelt (P3-2), `focus()`/delegatesFocus (P3-3), die
 * Fokus-Rückgabe von me-dialog an einen beim Schliessen noch abgeschalteten Auslöser (P4-1,
 * je Frame bis 1 s) und `ausgeklappt` (aria-expanded, P4-2). Übrig sind nur Bequemlichkeiten
 * (`KiKnopf` mit Vorgabe `sekundaer` und `name`, `Suchfeld`, `Dateiwahl`) und seit me-design
 * fbd4cb3 `KiRegister` (Text des Überlauf-Knopfs, Übergang W540-R). P4-3 (Options-/
 * Kästchengruppe) ist seit P17 erledigt: `Optionen` (Entscheid 40). P4-4 (Markenfarbe am
 * Chip) ist seit P16 erledigt: `Chip farbe` (Entscheid 41).
 */

/**
 * Ruft `fn`, wenn der Fokus ein ME-Feld verlässt: `blur` am Host — das innere Feld meldet es
 * über die Schattengrenze (FocusEvent ist `composed`), die Hülle reicht selbst kein `onBlur`
 * durch. Für Felder, die erst beim Verlassen speichern (Belegdesigner, Positionen, Zahlplan).
 */
export function useBeimVerlassen(host: RefObject<HTMLElement | null>,
                                 fn: (() => void) | undefined): void {
  const neuester = useRef(fn);
  neuester.current = fn;
  useEffect(() => {
    const element = host.current;
    if (element === null) return undefined;
    const zuhoerer = (): void => { neuester.current?.(); };
    element.addEventListener("blur", zuhoerer);
    return () => element.removeEventListener("blur", zuhoerer);
  }, [host]);
}

/**
 * Ruft `fn` bei der Eingabetaste in einem einzeiligen ME-Feld — und zwar in der
 * EINFANGPHASE am Host, also VOR dem Feld selbst: `me-eingabe` sendet bei Enter das
 * umschließende `<form>` ab, ausser die Taste ist schon `defaultPrevented`. Ein Suchfeld
 * mitten in einer Maske soll suchen, nicht die Maske speichern.
 */
export function useEingabetaste(host: RefObject<HTMLElement | null>,
                                fn: (() => void) | undefined): void {
  const neuester = useRef(fn);
  neuester.current = fn;
  useEffect(() => {
    const element = host.current;
    if (element === null) return undefined;
    const zuhoerer = (e: KeyboardEvent): void => {
      if (e.key !== "Enter" || neuester.current === undefined) return;
      e.preventDefault();
      neuester.current();
    };
    element.addEventListener("keydown", zuhoerer, { capture: true });
    return () => element.removeEventListener("keydown", zuhoerer, { capture: true });
  }, [host]);
}

/** Das Element hinter `KiRegister` — für `zumErstenFehler()` (Belegdesigner). */
export type RegisterHost = HTMLElement & { zumErstenFehler(): string | null };

/**
 * Die Reiterleiste der Anwendung: `Register` (me-register). Hausregel 540/547 (Dirk 30./31.10.2026), me-design 9178536
 * (Entscheid 103): Der Überlauf-Knopf ist flächig wie ein inaktiver Reiter und zeigt von sich aus „mehr“ kursiv; mit Text
 * trägt er kein `aria-label`, er heißt, wie er aussieht. Die Anwendung setzt dafür nichts (kein `mehr-text`, keine
 * Texttabelle) — die Hülle bleibt als EIN Einstieg aller Reiterleisten, `hausregeln540Css.test.ts` verbietet das
 * direkte `Register`.
 */
export const KiRegister = forwardRef<RegisterHost, RegisterProps>(function KiRegister(p, ref): ReactElement {
  return <Register ref={ref} {...p} />;
});

export type KiKnopfProps = {
  /** Vorgabe `sekundaer`; `primaer` für den einen Hauptweg eines Bereichs, `gefahr` für
   *  unumkehrbare Aktionen (Löschen), `leise` für Verweise. */
  art?: KnopfProps["art"];
  /** Abgeschaltet (`disabled` am inneren Knopf) — ein Klick bewirkt dann nichts. */
  deaktiviert?: boolean | undefined;
  /**
   * Der zugängliche Name, wo der sichtbare Text allein nicht reicht („Ändern“ an jeder
   * Zeile → „Zahlungskondition für … ändern“, „▲“ → „Bezeichnung nach oben“). Er geht als
   * `aria-label` an den Host; me-knopf spiegelt ihn auf seinen inneren Knopf.
   */
  name?: string | undefined;
  /** Aufklapper (aria-expanded am inneren Knopf) — weggelassen ist der Knopf keiner. */
  ausgeklappt?: boolean | undefined;
  /** Einstiegsfokus beim Öffnen eines Dialogs (etwa „Abbrechen“ einer Gefahr-Frage). */
  autoFocus?: boolean | undefined;
  /** Entscheid 80: sichtbar 1,75 rem hoch (Karte, Filterreihe, Titelzeile), die Trefferfläche bleibt 2,75 rem. */
  kompakt?: boolean | undefined;
  /** `pille` (Entscheid 61d): runde Enden. */
  gestalt?: "pille" | undefined;
  className?: string | undefined;
  /** Ein eigener Ref auf den Host (`me-knopf`) — etwa für eine Fokus-Rückgabe von Hand; als Rückruf auch neben
   *  einem Sofort-Popup (`useSofortPopup`). */
  knopfRef?: Ref<HTMLElement> | undefined;
  /** Absenden-Knopf des umschließenden `<form>` (me-design 8f7e53e, Befund P6-2): Der Klick
   *  sendet das Formular ab (requestSubmit). Dann darf `onClick` fehlen — gehandelt wird
   *  im `onSubmit` des Formulars. */
  absenden?: boolean | undefined;
  /** Mit `href` ein Link im Knopfbild (me-design 8f7e53e, Befund P6-1) — der Browser lädt
   *  selbst; `download` (Dateiname, leer = aus der Adresse) und `ziel` (`target`). */
  href?: string | undefined;
  download?: string | undefined;
  ziel?: string | undefined;
  /** Der Klick; das Ereignis braucht nur, wer den Link selbst führt (`preventDefault`, etwa der Handbuch-Knopf). */
  onClick?: ((e: MouseEvent) => void) | undefined;
  children: ReactNode;
};

/**
 * Ein Knopf der Anwendung (`me-knopf`, Vorgabe `sekundaer`). Der Klick wird bei `deaktiviert`
 * zusätzlich hier verworfen (ein Klick auf den Host selbst erreicht den abgeschalteten
 * inneren Knopf nicht, wohl aber den Rückruf).
 */
export function KiKnopf(p: KiKnopfProps): ReactElement {
  const aus = p.deaktiviert === true;
  return (
    <Knopf art={p.art ?? "sekundaer"} deaktiviert={aus}
      {...(p.autoFocus === true ? { autoFocus: true } : {})}
      {...(p.kompakt === true ? { kompakt: true } : {})}
      {...(p.gestalt === undefined ? {} : { gestalt: p.gestalt })}
      {...(p.knopfRef === undefined ? {} : { ref: p.knopfRef })}
      {...(p.className === undefined ? {} : { className: p.className })}
      // Ein weggefallenes aria-label räumt das Element selbst ab (me-design c0fc150, P5-1).
      {...(p.name === undefined ? {} : { "aria-label": p.name })}
      {...(p.ausgeklappt === undefined ? {} : { ausgeklappt: p.ausgeklappt })}
      {...(p.absenden === true ? { absenden: true } : {})}
      {...(p.href === undefined ? {} : { href: p.href })}
      {...(p.download === undefined ? {} : { download: p.download })}
      {...(p.ziel === undefined ? {} : { ziel: p.ziel })}
      onClick={(e) => { if (!aus) p.onClick?.(e); }}>
      {p.children}
    </Knopf>
  );
}

/**
 * „Löschen“ in einer Zeile (Entscheid 43, Dirk 17.10.2026; me-design „Tabelle ›
 * Bearbeitungstabelle“): ein LEISER Knopf mit Papierkorb, der Name sagt, was gelöscht wird
 * („Position 3 löschen“, „rechnung.pdf löschen“). Rot (`gefahr`) bleibt Bestätigungen und
 * Einzelaktionen vorbehalten — eine Liste mit zwanzig roten Knöpfen schreit, ohne dass etwas
 * passiert ist.
 */
export function LoeschKnopf(p: {
  name: string; deaktiviert?: boolean | undefined; className?: string | undefined;
  onClick: () => void;
}): ReactElement {
  return (
    <KiKnopf art="leise" name={p.name} deaktiviert={p.deaktiviert}
      className={`ki-loeschen${p.className === undefined ? "" : ` ${p.className}`}`}
      onClick={p.onClick}>
      <Papierkorb />
    </KiKnopf>
  );
}

/** Der Papierkorb der Story „Tabelle › Bearbeitungstabelle“ (me-design a120333, Dirk):
 *  22 px, Strich 1,4, in der Schriftfarbe des Knopfs; stumm, den Namen trägt der Knopf. */
function Papierkorb(): ReactElement {
  return (
    <svg className="ki-papierkorb" viewBox="0 0 16 16" width="22" height="22" fill="none"
      stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"
      aria-hidden="true" focusable="false">
      <path d="M2.5 4h11M6 4V2.5h4V4M4 4l.7 9.5h6.6L12 4M6.5 6.5v5M9.5 6.5v5" />
    </svg>
  );
}

export type SuchfeldProps = {
  id?: string | undefined;
  /** Der zugängliche Name („Kunden durchsuchen“) — ein Suchfeld hat keine sichtbare
   *  Beschriftung, das Element spiegelt ihn ins innere Feld. */
  name: string;
  platzhalter?: string | undefined;
  wert: string;
  onText: (text: string) => void;
  gesperrt?: boolean | undefined;
  className?: string | undefined;
  /** Eingabetaste: sucht (statt ein umschließendes Formular abzusenden). */
  onEingabetaste?: (() => void) | undefined;
  /** Sichtbare Beschriftung über dem Feld, wo das Suchfeld in einer Maske wie ein Feld steht
   *  („Kunden“ beim Anlegen einer Aufgabe); der Name bleibt `name`. */
  beschriftung?: string | undefined;
  /** Gedämpfter Hilfetext unter dem Feld (aria-describedby). */
  hilfe?: string | undefined;
};

/**
 * Ein Suchfeld (`me-eingabe art="suche"`, Befund 18): Löschkreuz und Suchtastatur des
 * Browsers, ohne sichtbare Beschriftung (die leere Beschriftungszeile des Elements blendet
 * `ki-suchfeld` aus; mit `beschriftung` steht sie wie an jedem Feld), Platzhalter über
 * `platzhalter` (me-design ba10760), Hilfetext über `hilfe`.
 */
export function Suchfeld(p: SuchfeldProps): ReactElement {
  const host = useRef<HTMLElement | null>(null);
  useEingabetaste(host, p.onEingabetaste);
  const klassen = ["ki-suchfeld", p.beschriftung === undefined ? "" : "ki-suchfeld--beschriftet",
    p.hilfe === undefined ? "" : "ki-suchfeld--hilfe", p.className ?? ""].filter((k) => k !== "").join(" ");
  return (
    <Eingabe ref={host} art="suche" aria-label={p.name} wert={p.wert} className={klassen}
      {...(p.id === undefined ? {} : { id: p.id })}
      {...(p.platzhalter === undefined ? {} : { platzhalter: p.platzhalter })}
      {...(p.beschriftung === undefined ? {} : { beschriftung: p.beschriftung })}
      {...(p.hilfe === undefined ? {} : { hilfe: p.hilfe })}
      {...(p.gesperrt === true ? { gesperrt: true } : {})}
      onGeaendert={(d) => { if (p.gesperrt !== true) p.onText(d.wert); }} />
  );
}

export type DateiwahlProps = {
  id?: string | undefined;
  /** Sichtbare Beschriftung des Elements (im `Formular` wie ein `Feld`). Ohne sie entfällt
   *  die Beschriftungszeile, den Namen trägt dann `name`. */
  beschriftung?: string | undefined;
  /** Zugänglicher Name ohne sichtbare Beschriftung („Logo von … hochladen“). */
  name?: string | undefined;
  accept?: string | undefined;
  mehrere?: boolean | undefined;
  /** Beschriftung des Wählknopfs (Vorgabe „Datei wählen“) — etwa „Ersetzen“/„Hochladen“
   *  an Logo und Unterschrift (me-design 6bfec97, Befund P4b-3). */
  knopftext?: string | undefined;
  pflicht?: boolean | undefined;
  /** Gesperrt (Klick, Tasten und Ablegen abgefangen, der Fokus bleibt) — etwa während des
   *  Hochladens. */
  gesperrt?: boolean | undefined;
  fehler?: string | undefined;
  hilfe?: string | undefined;
  className?: string | undefined;
  /**
   * Nach jeder Wahl leeren — für Auswahlen, die SOFORT hochladen (Logo, Unterschrift,
   * Grafik, Ablage): Dieselbe Datei lässt sich nach einem Fehler erneut wählen (sonst meldete
   * die gleiche Auswahl keine Änderung). Ohne Angabe bleibt die Wahl stehen (Masken, die erst
   * mit „Einlesen“ senden).
   */
  nachWahlLeeren?: boolean | undefined;
  onDateien: (dateien: File[]) => void;
};

/**
 * Eine Dateiauswahl (`me-eingabe art="datei"`, Entscheid 31): natives Dateifeld im Bild des
 * sekundären Knopfs, Tastatur, Vorlesestimme und Ablegen vom System. Nicht kontrolliert —
 * die Dateien kommen über `onDateien` (aus `me-geaendert`, `dateien`).
 */
export function Dateiwahl(p: DateiwahlProps): ReactElement {
  const host = useRef<HTMLElement | null>(null);
  const klassen = ["ki-dateiwahl", p.beschriftung === undefined ? "ki-dateiwahl--ohne-beschriftung" : "",
    p.className ?? ""].filter((k) => k !== "").join(" ");
  return (
    <Eingabe ref={host} art="datei" className={klassen}
      {...(p.id === undefined ? {} : { id: p.id })}
      {...(p.beschriftung === undefined ? {} : { beschriftung: p.beschriftung })}
      {...(p.name === undefined ? {} : { "aria-label": p.name })}
      {...(p.accept === undefined ? {} : { accept: p.accept })}
      {...(p.mehrere === true ? { mehrere: true } : {})}
      {...(p.knopftext === undefined ? {} : { knopftext: p.knopftext })}
      {...(p.pflicht === true ? { pflicht: true } : {})}
      {...(p.gesperrt === true ? { gesperrt: true } : {})}
      {...(p.fehler === undefined || p.fehler === "" ? {} : { fehler: p.fehler })}
      {...(p.hilfe === undefined ? {} : { hilfe: p.hilfe })}
      onGeaendert={(d) => {
        const dateien = d.dateien ?? [];
        // Leeren: `wert=""` von aussen setzt das Dateifeld des Elements zurück.
        if (p.nachWahlLeeren === true && dateien.length > 0) host.current?.setAttribute("wert", "");
        if (p.gesperrt !== true) p.onDateien(dateien);
      }} />
  );
}

export type NavigationsleisteProps = {
  /** Name der Navigation (aria-label, me-seitenleiste spiegelt ihn auf ihr `<nav>`). */
  name: string;
  className?: string;
  /** `Menueeintrag`-Kinder (davor höchstens ein Rückweg-Knopf). */
  children?: ReactNode;
};

/**
 * Die Navigationsspalte (ME-Etappe 4, P17, Entscheid 44): `me-seitenleiste` mit
 * `me-menueeintrag`-Kindern, für Verwaltung, Einstellungen und Schnittstellen.
 *
 * Die Leiste steht hier IMMER offen. me-seitenleiste klappt unter der Telefongrenze von
 * selbst ein — gerade dort ist die Spalte im Einspalter aber der ganze Schirm, und eine
 * 3,5 rem schmale Symbolleiste darin wäre kein Gewinn. Das Element schaltet seine
 * Automatik ab, sobald die Anwendung `eingeklappt` einmal selbst setzt oder entfernt;
 * genau das tut der Layout-Effekt (setzen und sofort wieder entfernen — ein Entfernen
 * allein meldet nichts, wenn das Attribut gar nicht stand). Eingeklappt (nur Symbol bzw.
 * Anfangsbuchstabe, Name im Sofort-Popup) zeichnet das Element selbst, sobald eine
 * Anwendungsstelle es braucht.
 */
export function Navigationsleiste(p: NavigationsleisteProps): ReactElement {
  const ref = useRef<HTMLElement | null>(null);
  useLayoutEffect(() => {
    const leiste = ref.current;
    if (leiste === null) return;
    leiste.setAttribute("eingeklappt", "");
    leiste.removeAttribute("eingeklappt");
  }, []);
  return (
    <Seitenleiste ref={ref} aria-label={p.name}
      className={`ki-navleiste${p.className === undefined ? "" : ` ${p.className}`}`}>
      {p.children}
    </Seitenleiste>
  );
}
